import React, { useMemo } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { Organization } from "@common/types/organization";
import { OrganizationUserRole } from "@common/userOrganizationRoles";
import { useWorkspacesList } from "@api/workspace.api";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { useInviteOrganizationMemberMutation } from "@api/organizationMember.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import styles from "@components/memberManagement/MemberManagement.module.scss";
import { AddOrganizationMemberFormSchema } from "./types";
import { AddOrganizationTeamFields } from "./AddOrganizationTeamFields";

const addOrganizationMemberFormSchema = z.object({
    emails: z
        .array(z.string().email("All emails must be valid"))
        .min(1, "At least one email is required"),
    organizationRole: z.nativeEnum(OrganizationUserRole),
    workspaceAssignments: z.record(z.string(), z.boolean()),
    assignToNewClients: z.boolean(),
});

interface Props {
    organization: Organization;
    providedEmail?: string;
    providedRole?: OrganizationUserRole;
    initialWorkspaceIds?: string[];
    closeModal: () => void;
    onInvited?: () => void;
}

export const AddOrganizationMemberModal: React.FC<Props> = ({
    organization,
    providedEmail,
    providedRole,
    initialWorkspaceIds,
    onInvited,
    closeModal,
}) => {
    const { toast } = useToaster();
    const inviteMemberMutation = useInviteOrganizationMemberMutation(
        organization.id,
    );
    const workspacesQuery = useWorkspacesList();

    const workspaces = useMemo(
        () => workspacesQuery.data ?? [],
        [workspacesQuery.data],
    );

    const sortedWorkspaces = useMemo(
        () =>
            [...workspaces].sort((a, b) => {
                const aIsInitial = initialWorkspaceIds?.includes(a.id) ?? false;
                const bIsInitial = initialWorkspaceIds?.includes(b.id) ?? false;
                if (aIsInitial && !bIsInitial) {
                    return -1;
                }
                if (!aIsInitial && bIsInitial) {
                    return 1;
                }
                return a.name.localeCompare(b.name);
            }),
        [workspaces, initialWorkspaceIds],
    );

    const formik = useFormik<AddOrganizationMemberFormSchema>({
        initialValues: {
            emails: providedEmail ? [providedEmail] : [],
            organizationRole: providedRole ?? OrganizationUserRole.MEMBER,
            workspaceAssignments: sortedWorkspaces.reduce(
                (acc, workspace) => {
                    acc[workspace.id] = initialWorkspaceIds
                        ? initialWorkspaceIds.includes(workspace.id)
                        : true;
                    return acc;
                },
                {} as Record<string, boolean>,
            ),
            assignToNewClients: false,
        },
        validate: validateFormikWithZodSchema(addOrganizationMemberFormSchema),
        onSubmit: submitHelper({
            handler: async ({
                emails,
                organizationRole,
                workspaceAssignments,
                assignToNewClients,
            }) => {
                await inviteMemberMutation.mutateAsync({
                    emails,
                    role: organizationRole,
                    workspaceAssignments: Object.keys(
                        workspaceAssignments,
                    ).filter((id) => workspaceAssignments[id]),
                    assignToNewClients,
                });

                onInvited?.();
                closeModal();
                toast(`Invite sent to ${emails.join(", ")}`);
            },
        }),
    });

    return (
        <>
            <StandardModalHeader>
                Invite member to {organization.name}
            </StandardModalHeader>

            <section className={styles.layout}>
                <FormikProvider value={formik}>
                    <Form>
                        <main className={styles.teamForm}>
                            <AddOrganizationTeamFields
                                organization={organization}
                                workspaces={sortedWorkspaces}
                            />

                            <footer className={styles.footer}>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="xl"
                                    onClick={closeModal}
                                    disabled={formik.isSubmitting}
                                >
                                    Cancel
                                </Button>

                                <ButtonWithLoader
                                    loading={formik.isSubmitting}
                                    type="submit"
                                    variant="primary"
                                    size="xl"
                                    disabled={!formik.isValid}
                                    hotKey="mod+enter"
                                    hotKeyOptions={{
                                        description: "Send invites",
                                        enableOnFormTags: true,
                                    }}
                                >
                                    Send invites
                                </ButtonWithLoader>
                            </footer>
                        </main>
                    </Form>
                </FormikProvider>
            </section>
        </>
    );
};
